import {
  useRouteBodyState,
  useRouteBodyRef,
} from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { useEffect, useMemo, useRef, type Dispatch, type SetStateAction } from "react";
import { useAdminAccess } from "@-/dev.improve.haniverse.lib.admin.access";
import type { MorphemePanelsToolbarEditAction } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels";

export type MorphemeMnemonicsPanelContextValue = {
  activeIndex: number;
  isEditing: boolean;
  /** A card-actions menu asked to edit the flashcard; the meaning reading starts editing. */
  isFlashcardEditPending: boolean;
  onFlashcardEditPendingChange: ((isPending: boolean) => void) | undefined;
  onToolbarEditActionChange: ((action: MorphemePanelsToolbarEditAction) => void) | undefined;
  setActiveIndex: Dispatch<SetStateAction<number>>;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
  tabCount: number;
};

export function useMorphemeMnemonicsPanelContextValue({
  advancePronunciationRequestId,
  grapheme,
  isFlashcardEditPending = false,
  onFlashcardEditPendingChange,
  onToolbarEditActionChange,
  tabCount,
}: {
  advancePronunciationRequestId: number;
  grapheme: string;
  isFlashcardEditPending?: boolean;
  onFlashcardEditPendingChange?: (isPending: boolean) => void;
  onToolbarEditActionChange?: (action: MorphemePanelsToolbarEditAction) => void;
  tabCount: number;
}) {
  const [activeIndex, setActiveIndex] = useRouteBodyState("mnemonic-active-index", 0);
  const isAdmin = useAdminAccess();
  const [editingRequested, setIsEditing] = useRouteBodyState("mnemonic-editing", false);
  const isEditing = isAdmin && editingRequested;

  const previousGrapheme = useRouteBodyRef("mnemonic-grapheme", grapheme);
  useEffect(() => {
    if (previousGrapheme.current === grapheme) {
      return;
    }
    previousGrapheme.current = grapheme;
    setActiveIndex(0);
    setIsEditing(false);
  }, [grapheme, previousGrapheme, setActiveIndex, setIsEditing]);

  const previousAdvancePronunciationRequestIdRef = useRef(advancePronunciationRequestId);

  useEffect(() => {
    if (previousAdvancePronunciationRequestIdRef.current === advancePronunciationRequestId) {
      return;
    }
    previousAdvancePronunciationRequestIdRef.current = advancePronunciationRequestId;
    setActiveIndex((previousActiveIndex) => Math.min(previousActiveIndex + 1, tabCount - 1));
  }, [advancePronunciationRequestId, tabCount, setActiveIndex]);

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, Math.max(0, tabCount - 1)));
  }, [tabCount, setActiveIndex]);

  return useMemo(
    (): MorphemeMnemonicsPanelContextValue => ({
      activeIndex,
      isEditing,
      isFlashcardEditPending,
      onFlashcardEditPendingChange,
      onToolbarEditActionChange,
      setActiveIndex,
      setIsEditing,
      tabCount,
    }),
    [
      activeIndex,
      setActiveIndex,
      tabCount,
      isEditing,
      isFlashcardEditPending,
      onFlashcardEditPendingChange,
      onToolbarEditActionChange,
      setIsEditing,
    ],
  );
}
